/* eslint-disable indent */
/* eslint-disable array-bracket-newline */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Skeleton, colors } from 'syngenta-digital-cropwise-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useAppState } from 'context/AppState';
import { MainSectionContent, StyledTextInfo } from './ExpertSeedAdvice.styles';
import { ProductsContainer, DisclaimerContentText } from './styles';
import SeedCard, { IProps as SeedCardProps } from './SeedCard';
import { ProductSeed } from 'base/types/Products';
import { getTraitLabel } from 'utils/trait';
import { CropConstants } from 'utils/constants/Crop';
import {
  averageYieldConvertedValue,
  convertYieldFromQaHectareToKgDecare,
  seedRateConvertedValue,
} from 'utils/helpers/converter';
import {
  SkeletonRowStyled,
  SkeletonTitleStyled,
  SkeletonMobileContainer,
  SkeletonMobileBottomContainer,
  SkeletonMobileTopContainer,
} from '../SoilCharacteristics.styles';
import { BreakoutsProductsSummaryDetails } from 'base/types/BreakoutsProductsSummaryResponse';
import {
  ExpertSeedAdviceTitle,
  LinkContainer,
  SectionContainer,
  StyledTooltip,
  Text,
  TooltipContainer,
  TooltipLink,
  TooltipText,
  TooltipTitle,
  TooltipValueText,
} from 'pages/RecommendationV2/V2Common.styles';
import BottomSheet from '../AgronomicPreferencesSheet/AgronomicPreferencesSheet';
import { getDataForSouthAfrica } from 'utils/getSeedRateAndBagSizeForSouthAfrica';
import { isBrazil } from 'utils/countryCode';
import brazilCornCatalog from '../../../../brazil-corn-catalog.json';

interface BrazilCornCatalogEntry {
  product: string;
  headline: string;
  highlights: string[];
  characteristics: Record<string, string>;
}

interface IProps {
  readonly sectionRef?: any;
  readonly isMobile?: boolean;
  readonly isRecommendationAPILoading?: boolean;
  readonly isLoading?: boolean;
  readonly isExpertSeedAdvice?: boolean;
  readonly isSecondSection?: boolean;
  readonly onClickToSeeDetails?: () => void;
  readonly handleOnClick?: (productName: string) => void;
}
export default function ExpertSeedAdvice({
  sectionRef,
  isMobile = false,
  onClickToSeeDetails,
  handleOnClick,
  isRecommendationAPILoading,
}: IProps): JSX.Element {
  const { t } = useTranslation();
  const {
    apiData: {
      recommendationCropwise,
      productCatalog: productsByCropAndCountry,
      breakoutsGlobal,
      breakoutsGlobalWithFilters,
      breakoutsProductsSummary,
      breakoutsProductsSummaryWithFilters,
      countryList,
      productCatalogLoading,
      breakoutsProductsSummaryLoading,
    },
  } = useAppState();

  const [products, setProducts] = useState<SeedCardProps[]>([]);
  const [showAgroPreferenceSheet, setShowAgroPreferenceSheet] = useState<boolean>(false);

  const cropName = recommendationCropwise?.recommendations[0].crop_name ?? '';
  const agronomicInputs =
    recommendationCropwise?.recommendations[0]?.multi_field?.config?.agronomicInputs;
  const {
    yieldRange,
    rotation: plantingPeriod,
    tillage: tillagePractice,
    spatialClass,
  } = agronomicInputs ?? {};
  const isDDG = recommendationCropwise?.source === 'DDG';
  const yieldUnit = agronomicInputs?.extraInfo?.yieldRangeUnit ?? '';
  const calculateSeedRate = (country: string | undefined, seedRate: string, product: string) => {
    if (country === CropConstants.SOUTH_AFRICA) {
      return getDataForSouthAfrica(country, spatialClass, product, countryList, true);
    } else {
      return seedRateConvertedValue(country, seedRate);
    }
  };

  const generateDescription = (country: string | undefined, dataList: string) => {
    if (country === CropConstants.SOUTH_AFRICA) {
      return dataList?.split('•').filter((data) => data !== '');
    }
    return dataList;
  };

  useEffect(() => {
    if (isRecommendationAPILoading) {
      return;
    }
    const productsByCropAndCountryMapByProductName: { [key: string]: ProductSeed } =
      productsByCropAndCountry.reduce(
        (accumulator, product) => ({
          ...accumulator,
          [product.productName]: { ...product },
        }),
        {}
      );

    const recommendedProducts = recommendationCropwise?.recommendations[0].products ?? [];

    const country: string | undefined = recommendationCropwise?.country.trim();
    const recommendationProducts = recommendedProducts.filter(
      (seed) => seed.product_name !== 'None'
    );

    const productsFormatted: SeedCardProps[] = recommendationProducts.map((product, index) => {
      //FOR BRAZIL NK, we recieve product name as 8 and 9 but in catalog we have it as proper name need to fix it.
      //FOR BRAZIL, we receive the product name with spaces from the API, but in the product catalog, the product names are without spaces, so we need to remove the spaces to find the correct product in the catalog. For other countries, we can use the product name as it is.
      const productName = isBrazil(country)
        ? product.commercial_name?.replace(/\s/g, '')
        : product.product_name;

      const productMapped = productsByCropAndCountryMapByProductName[productName] || {
        commercialName: '',
      };

      const productsSummary = breakoutsProductsSummary?.find(
        (item: BreakoutsProductsSummaryDetails) => item.entry === productName
      );
      const productsSummaryFiltered = breakoutsProductsSummaryWithFilters?.find(
        (item: BreakoutsProductsSummaryDetails) => item.entry === productName
      );

      const avgYield = productsSummaryFiltered?.balancedYieldAverage
        ? productsSummaryFiltered?.balancedYieldAverage
        : productsSummaryFiltered?.avgYieldSummary;

      const brazilCornEntry: BrazilCornCatalogEntry | undefined =
        isBrazil(country) && cropName?.toLowerCase().includes('corn') && productName
          ? (brazilCornCatalog as BrazilCornCatalogEntry[]).find(
              (entry) => entry.product.toLowerCase() === productName.toLowerCase()
            )
          : undefined;

      const data = {
        productName: productMapped.productName,
        fao: productMapped.fao,
        commercialName: productMapped.commercialName,
        headline: brazilCornEntry?.headline,
        highlights: brazilCornEntry?.highlights,
        characteristics: brazilCornEntry?.characteristics,
        seedRate: calculateSeedRate(
          country,
          productMapped.defaultSeedingRate,
          product?.commercial_name ?? ''
        ),
        description: generateDescription(country, productMapped.positioning),
        avgYieldSummary:
          country === CropConstants.Bulgaria
            ? convertYieldFromQaHectareToKgDecare(avgYield).toFixed(1)
            : avgYield,
        plusMinCheck: productsSummary?.plusMinCheck,
        winSummary: productsSummaryFiltered?.winSummary,
        locationsYieldData: productsSummaryFiltered?.locationsSummary,
        locationSummary: productsSummary?.locationsSummary,
        isSelected: !index,
        technology: productMapped.technology,
        grainType: productMapped.grainType,
        grainYieldType:
          productMapped.country?.name === 'Germany' && productMapped.crop?.name === 'Barley'
            ? productMapped.grainYieldType
            : null,
        virusResistance: [
          ...(productMapped.barleyYellowDwarfVirus >= 7 ? ['BYDV'] : []),
          ...(productMapped.barleyYellowMosaicVirus
            ? productMapped.barleyYellowMosaicVirus.split(';')
            : []),
        ].join(', '),
        color: product.color,
        isDDG: isDDG,
        unit: yieldUnit,
      };

      const additionalData = cropName === CropConstants.SUNFLOWER_SMALLCASE && {
        marketSegment: productMapped.segment,
        herbicideTechnology: getTraitLabel(productMapped.trait),
        maturity: productMapped.maturityGroup,
        broomrapeTechnology: productMapped?.broomrape?.replace('BR ', '') ?? '',
      };

      return {
        ...data,
        ...additionalData,
      };
    });

    setProducts([...productsFormatted]);
  }, [
    productsByCropAndCountry,
    breakoutsGlobal,
    breakoutsGlobalWithFilters,
    breakoutsProductsSummary,
    breakoutsProductsSummaryWithFilters,
    recommendationCropwise,
    cropName,
    isRecommendationAPILoading,
  ]);

  const handleAgroPreferencesClick = (value: boolean) => {
    setShowAgroPreferenceSheet(value);
  };

  const getFieldDetail = (_id: string) => {
    if (recommendationCropwise) {
      const field =
        recommendationCropwise.recommendations[0].multi_field.config.recommendation.find(
          (item) => item.FieldId === _id
        );
      if (field) {
        const product = recommendationCropwise.recommendations[0].products.find(
          (item) => item.product_name === field.SelectedSeed?.SeedName
        );
        return {
          commercialName: product?.commercial_name,
        };
      }
      return undefined;
    }
  };

  const additionalFieldDetail =
    recommendationCropwise?.recommendations?.[0]?.multi_field?.geometry?.properties?.map(
      (property) => {
        const fields = property.fields.map((field) => {
          const commercialName = getFieldDetail(field.id)?.commercialName ?? '';
          return { commercialName };
        });
        return { fields };
      }
    );

  const ProductRecommendatedFor = (commercialName: string | undefined) => {
    if (additionalFieldDetail && additionalFieldDetail.length > 0) {
      return additionalFieldDetail
        .map(
          (detail) =>
            detail.fields.filter((field) => field.commercialName === commercialName).length
        )
        .reduce((sum, count) => sum + count, 0);
    }

    return 0;
  };

  const [firstElement, ...remainingElemnts] = products;
  const renderSeedCards = useCallback(() => {
    if (isDDG) {
      return [
        <SeedCard
          isMobile={isMobile}
          key="top-rated"
          cardTitle={`${t('TOP RATED PRODUCT')}`}
          cropName={cropName}
          countryName={recommendationCropwise?.country.trim()}
          index={0}
          handleOnClick={handleOnClick}
          size={products.length}
          productCatalogLoading={productCatalogLoading}
          isLoadingBreakoutsProductSummaryAPI={breakoutsProductsSummaryLoading}
          {...firstElement}
        />,
        ...remainingElemnts.map((data, index) => (
          <SeedCard
            isMobile={isMobile}
            key={data.productName}
            cardTitle={`${t('ALTERNATIVE PRODUCT')}`}
            cropName={cropName}
            countryName={recommendationCropwise?.country.trim()}
            index={index}
            handleOnClick={handleOnClick}
            size={products.length}
            productCatalogLoading={productCatalogLoading}
            isLoadingBreakoutsProductSummaryAPI={breakoutsProductsSummaryLoading}
            {...data}
          />
        )),
      ];
    } else {
      return [
        products.map((data, index) => (
          <SeedCard
            isMobile={isMobile}
            key={data.productName}
            cardTitle={`${t('OUR RECOMMENDATION')}`}
            cropName={cropName}
            countryName={recommendationCropwise?.country.trim()}
            index={index}
            handleOnClick={handleOnClick}
            recommendedFieldCount={ProductRecommendatedFor(data.commercialName)}
            size={products.length}
            productCatalogLoading={productCatalogLoading}
            isLoadingBreakoutsProductSummaryAPI={breakoutsProductsSummaryLoading}
            {...data}
          />
        )),
      ];
    }
  }, [firstElement, remainingElemnts, isMobile, t, cropName, breakoutsProductsSummaryLoading]);

  const getUserMsgForFAOAdjustment = useCallback(() => {
    if (agronomicInputs) {
      const productCount = agronomicInputs?.productCountBasedOnUserSelectedFAO;
      if (productCount === 0) {
        return (
          <StyledTextInfo>
            {t(
              'Within the FAO range you provided there have been no products found. We have adjusted the range.'
            )}
          </StyledTextInfo>
        );
      }
      if (productCount === 1 || productCount === 2) {
        return (
          <StyledTextInfo>
            {t(
              'Within the FAO range you provided there have been limited number of products found. We have adjusted the range.'
            )}
          </StyledTextInfo>
        );
      }
    }
  }, [t, agronomicInputs]);

  const getUserMsgForBroomrapeFPlus = useCallback(() => {
    if (agronomicInputs) {
      if (
        agronomicInputs?.broomRapeUserSelection &&
        agronomicInputs?.broomRapeUserSelection[0] === 'F+'
      ) {
        return (
          <StyledTextInfo>
            {t(
              'Please note, that the below sunflower varieties are corresponding to the broomrape type F.'
            )}
          </StyledTextInfo>
        );
      }
    }
  }, [t, agronomicInputs]);

  const SeedCardsContainer = useCallback(() => {
    return (
      <>
        {getUserMsgForFAOAdjustment()}
        {cropName === CropConstants.SUNFLOWER_SMALLCASE && getUserMsgForBroomrapeFPlus()}

        <ProductsContainer center={isMobile ?? products.length < 3}>
          {renderSeedCards()}
        </ProductsContainer>
      </>
    );
  }, [
    isMobile,
    products,
    renderSeedCards,
    getUserMsgForFAOAdjustment,
    getUserMsgForBroomrapeFPlus,
  ]);

  const tooltipProps = {
    title: (
      <TooltipContainer data-testid={'agronomic-inputs-tooltip'}>
        <TooltipTitle>{t('Agronomic Inputs')}:</TooltipTitle>
        <TooltipText>
          {t('Historic yield range')}
          <TooltipValueText>: {yieldRange}</TooltipValueText>
        </TooltipText>
        {plantingPeriod && (
          <TooltipText>
            {t('Planting period')}
            <TooltipValueText>: {plantingPeriod}</TooltipValueText>
          </TooltipText>
        )}
        {tillagePractice && (
          <TooltipText>
            {t('Tillage Practice')}
            <TooltipValueText>: {tillagePractice}</TooltipValueText>
          </TooltipText>
        )}

        <TooltipLink onClick={onClickToSeeDetails}>{t('Click to See Details')}</TooltipLink>
      </TooltipContainer>
    ),
  };

  const getExpertSeedAdviceDescription = (productCount: number) => {
    return (
      <>
        {t(
          'Here are Top products, that have proven their performance under your environmental conditions and match your'
        )}
        &nbsp;
        {isMobile ? (
          <LinkContainer onClick={() => handleAgroPreferencesClick(true)}>
            {t('agronomic preferences.')}
          </LinkContainer>
        ) : (
          <StyledTooltip color={colors.neutral90} placement="topRight" {...tooltipProps}>
            <LinkContainer>{t('agronomic preferences.')}</LinkContainer>
          </StyledTooltip>
        )}
      </>
    );
  };

  const SkeletonLoader = useMemo(() => {
    if (isMobile) {
      return (
        <SkeletonMobileContainer>
          <SkeletonMobileTopContainer>
            <SkeletonTitleStyled width="80%" active size="large" />
          </SkeletonMobileTopContainer>
          <SkeletonMobileBottomContainer>
            <SkeletonRowStyled active size="large" />
          </SkeletonMobileBottomContainer>
        </SkeletonMobileContainer>
      );
    }
    return <Skeleton active paragraph={{ rows: 10, width: '100%' }} />;
  }, [isMobile]);

  return (
    <SectionContainer data-testid="recommended-products-section" ref={sectionRef}>
      <MainSectionContent isHeaderSection={true}>
        <ExpertSeedAdviceTitle>{t('Recommended Products')}</ExpertSeedAdviceTitle>
        {isRecommendationAPILoading ? (
          SkeletonLoader
        ) : (
          <Text>{getExpertSeedAdviceDescription(products.length)}</Text>
        )}
      </MainSectionContent>
      {isRecommendationAPILoading ? (
        SkeletonLoader
      ) : (
        <>
          {isMobile && showAgroPreferenceSheet && (
            <BottomSheet
              title={t('Agronomic Inputs')}
              onClickOutside={() => handleAgroPreferencesClick(false)}
              onClickViewDetails={() => onClickToSeeDetails && onClickToSeeDetails()}
              yieldRange={yieldRange}
              plantingPeriod={plantingPeriod}
              tillagePractice={tillagePractice}
            />
          )}
          <SectionContainer data-testid="Seed-cards-container">
            {SeedCardsContainer()}
          </SectionContainer>
          <MainSectionContent isHeaderSection={false}>
            <DisclaimerContentText>
              {t('*Relative yield advantage vs other tested products')}
            </DisclaimerContentText>
          </MainSectionContent>
        </>
      )}
    </SectionContainer>
  );
}
